import { ActionTree, GetterTree, MutationTree } from 'vuex'
import Vue from 'vue'
import {
  GetLocalStorage,
  SetLocalStorage,
} from '~/assets/ts/utils/localstorage'
import { GetDefaultWebPreferences, WebPreferences } from '~/models/users/user'

export enum UserSearchType {
  Sermons = 'sermons',
  Broadcasters = 'broadcasters',
  Speakers = 'speakers',
  Series = 'series',
  Videos = 'videos',
  Articles = 'articles',
  Transcripts = 'transcripts',
  Scripture = 'bible',
  Page = 'page',
}

export interface UserSearchParams {
  q: string
  type?: UserSearchType
  url?: string
}

export interface UserSearch extends UserSearchParams {
  count: number
}

const MAX_SEARCH_COUNT = 200

const MyChurchID = 'my-church.broadcaster-id'
const WebPreferencesID = 'preferences'
const SearchID = 'search'

export const state = () => ({
  myChurchBroadcasterID: '',
  webPreferences: {} as WebPreferences,
  searches: [] as UserSearch[],
})

export type LocalState = ReturnType<typeof state>
export const mutations: MutationTree<LocalState> = {
  SET_MY_CHURCH_BROADCASTER_ID: (state, broadcaster: string) => {
    state.myChurchBroadcasterID = SetLocalStorage(MyChurchID, broadcaster)
  },
  SET_WEB_PREFERENCES: (state, preferences: WebPreferences) => {
    Vue.set(
      state,
      'webPreferences',
      SetLocalStorage(WebPreferencesID, preferences)
    )
  },
  INITIALIZE: (state) => {
    state.myChurchBroadcasterID = GetLocalStorage(MyChurchID, '')
    state.webPreferences = GetLocalStorage(
      WebPreferencesID,
      GetDefaultWebPreferences()
    )
    state.searches = GetLocalStorage(SearchID, [])
  },
  REMOVE_SEARCH: (state, { q, type, url }: UserSearchParams) => {
    const searches = state.searches
    const foundIndex = searches.findIndex(
      (s) => s.q === q && type === s.type && url === s.url
    )
    if (foundIndex === -1) return
    searches.splice(foundIndex, 1)
    Vue.set(state, 'searches', SetLocalStorage(SearchID, searches))
  },
  ADD_SEARCH: (state, { q, type, url }: UserSearchParams) => {
    const searches = state.searches
    const foundIndex = searches.findIndex(
      (s) =>
        s.q.toLowerCase() === q.trim().toLowerCase() &&
        s.type === type &&
        url === s.url
    )

    if (foundIndex !== -1) {
      const found = searches[foundIndex]
      // Remove from current position
      searches.splice(foundIndex, 1)
      // Push updated object to the end
      searches.push({ q: q.trim(), type, url, count: found.count + 1 })
    } else {
      // Add new entry to the end
      searches.push({ q: q.trim(), type, url, count: 1 })
    }

    // Cap the array, trimming from the top
    if (searches.length > MAX_SEARCH_COUNT) {
      searches.splice(0, searches.length - MAX_SEARCH_COUNT)
    }

    // Persist and ensure reactivity
    Vue.set(state, 'searches', SetLocalStorage(SearchID, searches))
  },
}

export const getters: GetterTree<LocalState, LocalState> = {
  myChurchBroadcasterID: (state) => state.myChurchBroadcasterID,
  webPreferences: (state) => state.webPreferences,
  userSearches: (state) => state.searches,
}

export const actions: ActionTree<LocalState, LocalState> = {
  nuxtClientInit({ commit }) {
    commit('INITIALIZE')
  },
  setMyChurchBroadcasterID({ commit }, broadcasterID: string) {
    commit('SET_MY_CHURCH_BROADCASTER_ID', broadcasterID)
  },
  setWebPreferences({ commit }, preferences: WebPreferences) {
    commit('SET_WEB_PREFERENCES', preferences)
  },
}
